/**
 * DOM-level Spanish translation for panel screens not yet wired with t().
 *
 * Retrofit localization: it walks visible text nodes and input placeholders and swaps exact,
 * trimmed English UI phrases for Spanish from the map below. Data (plates, driver names,
 * numbers, coordinates) never matches a key, so it is left untouched. Runs only when the
 * language is Spanish; switching language reloads the page, so there is nothing to undo.
 */
const ES: Record<string, string> = {
  // headers / page titles
  "Ride platform import": "Importar viajes de plataforma",
  "Two different documents: a Trips export reconciles against the GPS journeys; a Payments statement carries the real platform commission and driver earnings. Pick which one you are uploading.":
    "Dos documentos distintos: un export de Viajes se concilia con los trayectos GPS; un extracto de Pagos trae la comisión real de la plataforma y las ganancias por conductor. Elige cuál subes.",
  "Driver scorecard": "Ranking de conductores",
  "CCTV & evidence": "CCTV y evidencias",
  "Device health": "Estado de equipos",
  "Drivers & shifts": "Conductores y turnos",
  "Live map": "Mapa en vivo",
  "Reports": "Informes",
  "Vehicles": "Vehículos",
  "Journeys": "Trayectos",
  "Passengers": "Pasajeros",
  "Reconciliation": "Conciliación",
  "Settings": "Ajustes",
  "Inbox": "Bandeja",
  "Audit log": "Registro de auditoría",
  "New conversation": "Nueva conversación",

  // import page
  "Trips": "Viajes",
  "Payments": "Pagos",
  "Fresh CSV (new trips)": "CSV de ejemplo (viajes nuevos)",
  "Drop the Uber export here or click to browse": "Suelta el export de Uber aquí o haz clic para elegir",
  "Import & reconcile": "Importar y conciliar",
  "Import payments": "Importar pagos",
  "Importing…": "Importando…",
  "Importing & reconciling…": "Importando y conciliando…",
  "Import devices": "Importar dispositivos",
  "Import batches": "Lotes de importación",
  "No imports yet. Demo trips were seeded directly.": "Sin importaciones. Los viajes demo se cargaron directamente.",
  "Imported": "Importados",
  "Duplicates": "Duplicados",
  "Rejected": "Rechazados",
  "Gross fares": "Facturación bruta",
  "Platform fee": "Comisión de plataforma",
  "Real commission": "Comisión real",
  "Net paid": "Neto pagado",
  "Driver payments statement": "Extracto de pagos por conductor",
  "NOT TRIPS": "NO SON VIAJES",
  "Column mapping": "Mapeo de columnas",
  "Date format": "Formato de fecha",
  "Field": "Campo",

  // AI quota  (numbers are interpolated, so these go through t() in i18n.tsx, not here:
  //  "of" and "default" alone are far too generic for exact text-node matching)
  "AI / day": "IA / día",

  // common actions / states
  "Add Vehicle": "Añadir vehículo", "Add driver": "Añadir conductor", "Add record": "Añadir registro",
  "Cancel": "Cancelar", "Create": "Crear", "Edit": "Editar", "Delete": "Eliminar", "Save": "Guardar",
  "Done": "Hecho", "Back": "Volver", "Next": "Siguiente", "Connect": "Conectar",
  "Connect all": "Conectar todas", "Back to wall": "Volver al mural", "Delete for good": "Eliminar definitivamente",
  "New user": "Nuevo usuario", "Set password": "Poner contraseña", "Reset password": "Restablecer contraseña",
  "Generate": "Generar", "Refresh": "Actualizar", "Open": "Abrir", "Capture now": "Capturar ahora",

  // fleet vocabulary
  "Driver": "Conductor", "Drivers": "Conductores", "Vehicle": "Vehículo", "Device": "Equipo",
  "Driver on shift": "Conductor en turno", "Capabilities": "Capacidades", "Fuel": "Combustible",
  "Occupants": "Ocupantes", "Distance": "Distancia", "Duration": "Duración", "Flags": "Marcas",
  "Decision": "Decisión", "Decision history": "Historial de decisiones", "Note": "Nota", "Score": "Puntuación",
  "Make": "Marca", "Model": "Modelo", "Colour": "Color", "Currency": "Moneda", "Name": "Nombre",
  "Full name": "Nombre completo", "Email": "Correo", "Account": "Cuenta", "Region": "Región",
  "NO SHIFT": "SIN TURNO", "OFF SHIFT": "FUERA DE TURNO", "ACTIVE": "ACTIVO", "INACTIVE": "INACTIVO",
  "Detected": "Detectado", "Likely": "Probable", "No evidence": "Sin evidencia",
  "GPS journey": "Trayecto GPS", "GPS track": "Traza GPS", "GPS GAP": "HUECO GPS",
  "Manual event": "Evento manual", "Manual passenger event": "Evento manual de pasajero",
  "Occupants detected": "Ocupantes detectados",

  // CCTV: fragment requests, uploaded media, clip status
  "Ask for a specific moment": "Pedir un momento concreto",
  "The unit keeps its own recording. Give it a time and it uploads that fragment; it arrives when the vehicle next has signal, then appears under MEDIA and against the clip.":
    "El equipo guarda su propia grabaci\u00f3n. Dale una hora y sube ese fragmento; llega cuando el veh\u00edculo vuelve a tener cobertura y aparece en MEDIA y en el clip.",
  "Moment": "Momento",
  "Minutes": "Minutos",
  "Request this window": "Pedir este intervalo",
  "Requesting\u2026": "Solicitando\u2026",
  "Check upload": "Comprobar subida",
  "Open recording": "Abrir grabaci\u00f3n",
  "Placeholder": "Marcador",
  "Photos and video the unit itself has uploaded, straight from the camera platform. This is where a requested fragment lands.":
    "Fotos y v\u00eddeo que ha subido el propio equipo, directamente de la plataforma de c\u00e1mara. Aqu\u00ed es donde aterriza un fragmento solicitado.",
  "Nothing uploaded by this vehicle in the last 7 days": "Este veh\u00edculo no ha subido nada en los \u00faltimos 7 d\u00edas",
  "No evidence clips yet \u2014 request one from Trip Detail or History":
    "A\u00fan no hay clips de evidencia: p\u00eddelos desde el detalle de un viaje o desde Historial",

  // vehicle device / IMEI
  "NO IMEI": "SIN IMEI",
  "IMEI (15 digits)": "IMEI (15 dígitos)",
  "no device attached": "sin equipo asignado",
  "The IMEI links this vehicle to its GPS/camera unit and is what enables live video, remote capture and evidence clips. Leave it empty and set it later from the Vehicles table.":
    "El IMEI vincula este vehículo con su equipo GPS/cámara y es lo que habilita el vídeo en vivo, la captura remota y los clips de evidencia. Déjalo vacío y ponlo después desde la tabla de Vehículos.",

  // devices / tracksolid / settings
  "Device IMEI / unique ID": "IMEI / ID único del equipo", "Device name": "Nombre del equipo",
  "Device time zone": "Zona horaria del equipo", "Clock offset (seconds)": "Desfase de reloj (segundos)",
  "Licence plate (UK)": "Matrícula", "App key": "App key", "API key": "Clave API", "Base URL": "URL base",
  "Manager name": "Nombre del gestor", "Fleet name": "Nombre de la flota",
  "Name as shown in Uber (optional)": "Nombre tal como aparece en Uber (opcional)",
  "Enable daily schedule": "Activar envío diario", "Browser notification": "Notificación del navegador",
  "Enable browser notifications": "Activar notificaciones del navegador",
  "Daily PDF report by email": "Informe PDF diario por correo",
  "Language · Idioma": "Idioma", "All vehicles": "Todos los vehículos",
  "New password (min 6)": "Nueva contraseña (mín. 6)",

  // empty states
  "No vehicles yet. Use the wizard to add the first one.": "Aún no hay vehículos. Usa el asistente para añadir el primero.",
  "No driver activity in this window": "Sin actividad de conductores en este periodo",
  "No journeys in the last 24 h": "Sin trayectos en las últimas 24 h",
  "No camera events": "Sin eventos de cámara",
  "No camera is streaming right now.": "Ninguna cámara está transmitiendo ahora mismo.",
  "No passenger journeys inferred yet": "Aún no se han inferido trayectos con pasajero",
  "No recordings in window": "Sin grabaciones en el periodo",
  "Nothing matches these filters": "Nada coincide con estos filtros",
  "Nothing queued yet.": "Nada en cola todavía.",
  "No fleets yet": "Aún no hay flotas",
  "Only a super admin can manage fleets.": "Solo un superadmin puede gestionar flotas.",
  "No drivers yet — add them under Drivers & Shifts.": "Aún no hay conductores — añádelos en Conductores y turnos.",
  "No GPS track or coordinates available": "Sin traza GPS ni coordenadas disponibles",
  "No cost can be calculated yet.": "Aún no se puede calcular el coste.",
  "No fuel data on this driver's vehicles": "Sin datos de combustible en los vehículos de este conductor",
  "Demo data": "Datos de ejemplo",
  "Add demo data": "Añadir datos de ejemplo", "Clear demo data": "Borrar datos de ejemplo",
  "Refreshing…": "Actualizando…",
  "Adding…": "Añadiendo…", "Clearing…": "Borrando…",
  "Placeholder fleet (10 London taxis) to explore the panel. Clearing it removes ONLY these demo records — your real vehicles, imports and devices are never touched.":
    "Flota de ejemplo (10 taxis de Londres) para explorar el panel. Al borrar se eliminan SOLO estos registros de ejemplo; tus vehículos, importaciones y equipos reales no se tocan.",
};

const SKIP = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEXTAREA", "CODE", "PRE"]);

function translateText(root: Node) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(n) {
      const p = n.parentElement;
      if (!p || SKIP.has(p.tagName)) return NodeFilter.FILTER_REJECT;
      const t = n.nodeValue?.trim();
      if (!t || !(t in ES)) return NodeFilter.FILTER_SKIP;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const hits: Text[] = [];
  let cur = walker.nextNode();
  while (cur) { hits.push(cur as Text); cur = walker.nextNode(); }
  for (const node of hits) {
    const raw = node.nodeValue as string;
    const key = raw.trim();
    node.nodeValue = raw.replace(key, ES[key]);
  }
  // placeholders
  root instanceof Element && root.querySelectorAll?.("input[placeholder], textarea[placeholder]").forEach((el) => {
    const ph = el.getAttribute("placeholder")?.trim();
    if (ph && ph in ES) el.setAttribute("placeholder", ES[ph]);
  });
}

let observer: MutationObserver | null = null;

export function startAutoTranslate() {
  translateText(document.body);
  if (observer) return;
  // Coalesce into one frame, but never drop a batch: returning early while a frame was pending
  // discarded those mutation records outright, so anything React rendered in the meantime — most
  // of the panel, since every table arrives asynchronously — silently stayed in English.
  let queued = false;
  let pending: MutationRecord[] = [];
  observer = new MutationObserver((muts) => {
    pending.push(...muts);
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      const batch = pending;
      pending = [];
      for (const m of batch) m.addedNodes.forEach((n) => { if (n.nodeType === 1 || n.nodeType === 3) translateText(n); });
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

export function stopAutoTranslate() {
  observer?.disconnect();
  observer = null;
}
